import { type ProductData } from "../models/ProductData";
import { type ProductGroup, type SortDirection, type SortOption, type SRPSeriesTreeNode } from "../Search/models";
import { type BreadcrumbItem } from "../jspComponents/Breadcrumbs/NavigationBreadcrumbs";
import { type TabularColumnDefDTO, type TechnicalInfoDataDTO } from "~/Search/dto/SearchResultsDTO";
import type { FilterConfig } from "~/Search/filterConfigModels";

export type UID = string;

export type SrpProductData = ProductData & {
  cadUrl?: string;
  calcUrl?: string;
  hasCad?: boolean;
};

export interface ContentDataItem {
  content: string; // HTML
  id: string;
  title: string; // HTML
  label: string;
  imageUrl: string;
  breadcrumbs?: BreadcrumbItem[];
}

export interface AutosuggestProductData {
  url: string;
  primaryImage?: ImgData;
  preferred?: boolean;
  designation: string;
  oldDesignation?: string;
  shortDescription: string;
}

export interface ImgData {
  url: string;
  altText?: string;
  id?: number;
}

export interface FeatureData {
  name: string;
  abbreviation: string;
  technicalDescription: string;
  techFilterId: string;
  unit: string;
  values: string[];
  type: string;
  isConverted: boolean;
}

export interface SearchResultRow {
  id: number;
  productCode: string;
  variantProductCodes: string[];
  features: FeatureData[];
}

export interface SearchResultData {
  results: SearchResultRow[];
  products: SrpProductData[];
  pagination: PaginationData;
  sorts: (SortOption & {
    selected?: boolean;
  })[];
  selectedSort: {
    code: string;
    direction: SortDirection | null;
  };
  facets: FacetData[];
  content: {
    results: ContentDataItem[];
    pagination: PaginationData;
  };
  seriesTree: SRPSeriesTreeNode[] | null;
  hasTubularResults: boolean;
  technicalInfo: TechnicalInfoDataDTO;
  hasCampaignResults: boolean;

  filterConfig: FilterConfig;
  filterConfigImperial: FilterConfig;
  tabularViewColConfig: TabularColumnDefDTO[];
  tabularViewColConfigImperial: TabularColumnDefDTO[];

  fullTaxonomy: ProductGroup[];
  breadcrumbs: BreadcrumbData[];
}

export interface PaginationData {
  /**
   * Warning: 1-indexed!
   */
  currentPage: number;
  numberOfPages: number;
  totalNumberOfResults: number;
  pageSize: number;
}

export enum BreadcrumbType {
  FACET,
  TECH_VALUE,
}

export interface FacetBreadcrumb {
  type: BreadcrumbType.FACET;

  facetValueCode: string;
  facetCode: string;
  name: string;
  value: string;
}

export interface FacetBreadcrumbResponse extends FacetBreadcrumb {
  facetValueName: string;
}

export interface TechValueBreadcrumb {
  type: BreadcrumbType.TECH_VALUE;

  name: string;
  value: number;
  valueType: string;
  code: string;
  uom: string;
}

export type BreadcrumbData = FacetBreadcrumb | TechValueBreadcrumb;

export interface FacetData {
  code: string;
  name: string;
  values: FacetValueData[];
}

export interface FacetValueData {
  code: string;
  name: string;
  count: number;
  selected: boolean;
  description?: string;
}

export interface TechFilterValueData {
  id: UID;
  description?: string;
  code: string;
  name: string;
  uom: string;
}

export interface TechFilterGroupData {
  id: UID;
  label: string;
  techFilterValues: UID[];
}
